// src/components/Invoice/ProfessionalVATInvoice.jsx
import React, { useState, useEffect } from "react";
import axios from "axios";
import {
  PDFDownloadLink,
  Document,
  Page,
  Text,
  View,
  StyleSheet,
  pdf,
  Image,
  BlobProvider,
} from "@react-pdf/renderer";

// PDF Styles
const pdfStyles = StyleSheet.create({
  page: {
    padding: 10,
    fontSize: 8,
    fontFamily: "Helvetica",
  },
  header: {
    flexDirection: "row",
    justifyContent: "space-between",
    marginBottom: 10,
    borderBottomWidth: 2,
    borderBottomColor: "#0891b2",
    paddingBottom: 20,
  },
  companyName: {
    fontSize: 20,
    fontWeight: "bold",
    color: "#0891b2",
  },
  companyDetails: {
    fontSize: 10,
    color: "#666",
    marginTop: 5,
  },
  invoiceTitle: {
    fontSize: 24,
    fontWeight: "bold",
    color: "#0891b2",
    textAlign: "right",
  },
  subtitle: {
    fontSize: 12,
    color: "#666",
    textAlign: "right",
    marginTop: 5,
  },
  subtitle0: {
    fontSize: 10,
    color: "#666",
    textAlign: "right",
    marginTop: 5,
    fontWeight: "bold",
  },
  infoSection: {
    flexDirection: "row",
    justifyContent: "space-between",
    marginBottom: 10,
    backgroundColor: "#f8f9fa",
    padding: 5,
    borderRadius: 5,
  },
  infoBlock: {
    flex: 1,
  },
  infoLabel: {
    fontSize: 10,
    color: "#666",
    marginBottom: 3,
    textTransform: "uppercase",
  },
  infoValue: {
    fontSize: 12,
    fontWeight: "bold",
    color: "#333",
  },
  customerSection: {
    marginBottom: 30,
    padding: 15,
    backgroundColor: "#f8f9fa",
    borderRadius: 5,
  },
  table: {
    marginBottom: 20,
    borderWidth: 1,
    borderColor: "#ddd",
  },
  tableHeader: {
    flexDirection: "row",
    backgroundColor: "#0891b2",
    color: "white",
    padding: 10,
    fontWeight: "bold",
  },
  tableRow: {
    flexDirection: "row",
    borderBottomWidth: 1,
    borderBottomColor: "#ddd",
    padding: 8,
  },
  tableRowAlt: {
    backgroundColor: "#f9f9f9",
  },
  col1: { width: "35%", paddingHorizontal: 5 },
  col2: { width: "10%", textAlign: "center", paddingHorizontal: 5 },
  col3: { width: "15%", textAlign: "right", paddingHorizontal: 5 },
  col4: { width: "15%", textAlign: "right", paddingHorizontal: 5 },
  col5: { width: "10%", textAlign: "center", paddingHorizontal: 5 },
  col6: { width: "15%", textAlign: "right", paddingHorizontal: 5 },
  totals: {
    marginTop: 20,
    alignItems: "flex-end",
  },
  totalRow: {
    flexDirection: "row",
    padding: 5,
    width: "50%",
  },
  totalLabel: {
    flex: 1,
    textAlign: "right",
    marginRight: 10,
    fontSize: 11,
  },
  totalValue: {
    width: 120,
    textAlign: "right",
    fontSize: 11,
  },
  grandTotal: {
    fontSize: 14,
    fontWeight: "bold",
    color: "#0891b2",
    borderTopWidth: 2,
    borderTopColor: "#0891b2",
    paddingTop: 10,
    marginTop: 5,
  },
  notes: {
    marginTop: 30,
    padding: 15,
    backgroundColor: "#f8f9fa",
    borderRadius: 5,
  },
  notesTitle: {
    fontSize: 12,
    fontWeight: "bold",
    marginBottom: 8,
    color: "#0891b2",
  },
  notesText: {
    fontSize: 10,
    lineHeight: 1.4,
    color: "#666",
  },
  footer: {
    position: "absolute",
    bottom: 30,
    left: 30,
    right: 30,
    textAlign: "center",
    color: "#666",
    fontSize: 9,
    borderTopWidth: 1,
    borderTopColor: "#ddd",
    paddingTop: 10,
  },
  compliance: {
    fontSize: 8,
    color: "#0891b2",
    marginTop: 5,
  },
  logo: {
    width: 30,
    height: 30,
  },
});

// PDF Document Component
const InvoicePDFDocument = ({ invoiceData }) => {
  console.log('===== PDF DOCUMENT RENDER =====');
  console.log('Invoice Data Received in PDF:', invoiceData);
  console.log('Invoice Number in PDF Document:', invoiceData?.invoiceNo);
  console.log('==============================');

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat("en-AE", {
      style: "currency",
      currency: "AED",
      minimumFractionDigits: 2,
    }).format(amount || 0);
  };

  const formatDate = (dateString) => {
    if (!dateString) return "";
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString("en-GB", {
        day: "2-digit",
        month: "2-digit",
        year: "numeric",
      });
    } catch {
      return dateString;
    }
  };

  return (
    <Document>
      <Page size="A4" style={pdfStyles.page}>
        {/* Header */}
        <View style={pdfStyles.header}>
          <View>
            <Image src={invoiceData.company.logo} style={{ width: 90, height: 110, marginBottom: 5, marginLeft: 100 }} />
          </View>
          <View>
            <Text style={pdfStyles.invoiceTitle}>TAX INVOICE</Text>
            <Text style={pdfStyles.subtitle0}>Diamond Quality Construction L.L.C.SP</Text>
            <Text style={pdfStyles.subtitle}>TRN: {invoiceData.company.trn}</Text>
            <Text style={pdfStyles.subtitle}>Sharjah - Muweilah - University City Street</Text>
            <Text style={pdfStyles.subtitle}>Office No.: 602-58 Floor No.: 6 - Al Areej</Text>
            <Text style={pdfStyles.subtitle}>0557001522</Text>
            <Text style={pdfStyles.subtitle}>dqcc01@gmail.com</Text>
            <Text style={pdfStyles.subtitle}>www.dqcc.ae</Text>
          </View>
        </View>

        {/* Invoice Info */}
        <View style={pdfStyles.infoSection}>
          <View style={pdfStyles.infoBlock}>
            <Text style={pdfStyles.infoLabel}>Invoice Number</Text>
            <Text style={pdfStyles.infoValue}>{invoiceData.invoiceNo}</Text>
          </View>
          <View style={pdfStyles.infoBlock}>
            <Text style={pdfStyles.infoLabel}>Invoice Date</Text>
            <Text style={pdfStyles.infoValue}>
              {formatDate(invoiceData.invoiceDate)}
            </Text>
          </View>
          {/* <View style={pdfStyles.infoBlock}>
            <Text style={pdfStyles.infoLabel}>Due Date</Text>
            <Text style={pdfStyles.infoValue}>
              {formatDate(invoiceData.dueDate)}
            </Text>
          </View> */}
          <View style={pdfStyles.infoBlock}>
            <Text style={pdfStyles.infoLabel}>Payment Status</Text>
            <Text style={pdfStyles.infoValue}>{invoiceData.status}</Text>
          </View>
        </View>

        {/* Customer Details */}
        <View style={pdfStyles.customerSection}>
          <Text style={pdfStyles.infoLabel}>Bill To:</Text>
          <Text
            style={[pdfStyles.infoValue, { marginTop: 5, marginBottom: 3 }]}
          >
            {invoiceData.customerName}
          </Text>
          <Text style={pdfStyles.companyDetails}>
            {invoiceData.customerAddress}
          </Text>
          {invoiceData.customerTrn && (
            <Text style={pdfStyles.companyDetails}>
              TRN: {invoiceData.customerTrn}
            </Text>
          )}
          <Text style={pdfStyles.companyDetails}>
            {invoiceData.customerCity}, {invoiceData.customerCountry}
          </Text>
          {invoiceData.customerPhone && (
            <Text style={pdfStyles.companyDetails}>
              Phone: {invoiceData.customerPhone}
            </Text>
          )}
        </View>

        {/* Items Table */}
        <View style={pdfStyles.table}>
          <View style={pdfStyles.tableHeader}>
            <Text style={pdfStyles.col1}>Description</Text>
            <Text style={pdfStyles.col2}>Qty</Text>
            <Text style={pdfStyles.col3}>Unit Price</Text>
            <Text style={pdfStyles.col4}>Discount</Text>
            <Text style={pdfStyles.col5}>VAT</Text>
            <Text style={pdfStyles.col6}>Total</Text>
          </View>

          {invoiceData.items.map((item, index) => {
            const subtotal = item.quantity * item.unit_price;
            const discountAmount = item.discount_amount;
            const afterDiscount = subtotal - discountAmount;
            const vatAmount = afterDiscount * (item.vat_percent / 100);
            const total = afterDiscount + vatAmount;

            return (
              <View
                key={index}
                style={[
                  pdfStyles.tableRow,
                  index % 2 === 1 ? pdfStyles.tableRowAlt : {},
                ]}
              >
                <Text style={pdfStyles.col1}>{item.description}</Text>
                <Text style={pdfStyles.col2}>
                  {item.quantity} {item.unit}
                </Text>
                <Text style={pdfStyles.col3}>
                  {formatCurrency(item.unit_price)}
                </Text>
                <Text style={pdfStyles.col4}>
                  {formatCurrency(item.discount_amount)}
                </Text>
                <Text style={pdfStyles.col5}>{item.vat_percent}%</Text>
                <Text style={pdfStyles.col6}>{formatCurrency(total)}</Text>
              </View>
            );
          })}
        </View>

        {/* Totals Section */}
        <View style={pdfStyles.totals}>
          <View style={pdfStyles.totalRow}>
            <Text style={pdfStyles.totalLabel}>Subtotal:</Text>
            <Text style={pdfStyles.totalValue}>
              {formatCurrency(invoiceData.totals.subtotal)}
            </Text>
          </View>
          <View style={pdfStyles.totalRow}>
            <Text style={pdfStyles.totalLabel}>Total Discount:</Text>
            <Text style={pdfStyles.totalValue}>
              {formatCurrency(invoiceData.totals.totalDiscount)}
            </Text>
          </View>
          <View style={pdfStyles.totalRow}>
            <Text style={pdfStyles.totalLabel}>Taxable Amount:</Text>
            <Text style={pdfStyles.totalValue}>
              {formatCurrency(invoiceData.totals.taxableAmount)}
            </Text>
          </View>
          <View style={pdfStyles.totalRow}>
            <Text style={pdfStyles.totalLabel}>VAT Amount (5%):</Text>
            <Text style={pdfStyles.totalValue}>
              {formatCurrency(invoiceData.totals.vatTotal)}
            </Text>
          </View>
          <View style={[pdfStyles.totalRow, pdfStyles.grandTotal]}>
            <Text style={pdfStyles.totalLabel}>Total Amount Due:</Text>
            <Text style={pdfStyles.totalValue}>
              {formatCurrency(invoiceData.totals.totalDue)}
            </Text>
          </View>
        </View>

        {/* Notes */}
        {invoiceData.notes && (
          <View style={pdfStyles.notes}>
            <Text style={pdfStyles.notesTitle}>Notes / Terms:</Text>
            <Text style={pdfStyles.notesText}>{invoiceData.notes}</Text>
          </View>
        )}

        {/* Footer */}
        <View style={pdfStyles.footer}>
          <Text>
            This is a computer generated invoice
          </Text>
          {/* <Text style={pdfStyles.compliance}>
            🇦🇪 UAE VAT Compliant | Federal Tax Authority Approved | TRN:{" "}
            {invoiceData.company.trn}
          </Text> */}
        </View>
      </Page>
    </Document>
  );
};

const ProfessionalVATInvoice = ({
  onSubmit = () => {},
  initialData,
  companyInfo = null,
}) => {
  // Helper function to format date for HTML input (YYYY-MM-DD)
  const formatDateForInput = (date) => {
    return date.toISOString().split('T')[0];
  };

  // Calculate default dates
  const today = new Date();
  const tenDaysFromNow = new Date();
  tenDaysFromNow.setDate(today.getDate() + 10);

  const [formData, setFormData] = useState(
    initialData || {
      invoiceNo: "[AUTO]",
      invoiceDate: formatDateForInput(today),
      dueDate: formatDateForInput(tenDaysFromNow),
      mode: null,
      status: "Unpaid",
      customerId: null,

      // Customer Information
      customerName: "",
      customerTrn: "",
      customerPhone: "",
      customerAddress: "",
      customerCity: "",
      customerCountry: "",

      // Line Items
      items: [
        {
          description: "Service1",
          quantity: 1,
          unit: "Unit",
          unit_price: 0,
          discount_amount: 0,
          vat_percent: 5,
        },
      ],

      // Notes
      notes:
        "",
    },
  );

  const [company] = useState(
    companyInfo || {
      name: "Diamond Quality Constructions",
      trn: "104836477000001",
      address: "Sharja, UAE",
      logo: "/logo.jpeg",
    },
  );

  const [items, setItems] = useState(formData.items);
  const [showCustomerForm, setShowCustomerForm] = useState(false);
  const [errors, setErrors] = useState({});
  const [pdfData, setPdfData] = useState(null);
  const [shouldDownload, setShouldDownload] = useState(false);
  const [customers, setCustomers] = useState([]);
  const [customersLoading, setCustomersLoading] = useState(false);
  const [apiDebugResponse, setApiDebugResponse] = useState(null);
  const [showSaveModal, setShowSaveModal] = useState(false);
  const [pendingFormData, setPendingFormData] = useState(null);
  const [successMessage, setSuccessMessage] = useState(null);
  const apiUrl = process.env.REACT_APP_API_BASE_URL;



  // UAE Emirates list
  const emirates = [
    "Abu Dhabi",
    "Dubai",
    "Sharjah",
    "Ajman",
    "Umm Al Quwain",
    "Ras Al Khaimah",
    "Fujairah",
  ];

  // Fetch customers from API
  useEffect(() => {
    const fetchCustomers = async () => {
      setCustomersLoading(true);
      try {
        const response = await axios.get(`${apiUrl}/api/customers`);
        setCustomers(response.data);
      } catch (error) {
        console.error("Error fetching customers:", error);
        setCustomers([]);
      } finally {
        setCustomersLoading(false);
      }
    };

    fetchCustomers();
  }, [apiUrl]);

  // Calculate line item total
  const calculateItemTotal = (item) => {
    const subtotal = item.quantity * item.unit_price;
    const discountAmount = item.discount_amount;
    const afterDiscount = subtotal - discountAmount;
    const vatAmount = afterDiscount * (item.vat_percent / 100);
    return afterDiscount + vatAmount;
  };

  // Calculate totals
  const calculateTotals = () => {
    let subtotal = 0;
    let totalDiscount = 0;
    let taxableAmount = 0;
    let vatTotal = 0;

    items.forEach((item) => {
      const itemSubtotal = item.quantity * item.unit_price;
      const itemDiscount = item.discount_amount;
      const itemAfterDiscount = itemSubtotal - itemDiscount;
      const itemVat = itemAfterDiscount * (item.vat_percent / 100);

      subtotal += itemSubtotal;
      totalDiscount += itemDiscount;
      taxableAmount += itemAfterDiscount;
      vatTotal += itemVat;
    });

    const totalDue = taxableAmount + vatTotal;

    return {
      subtotal,
      totalDiscount,
      taxableAmount,
      vatTotal,
      totalDue,
    };
  };

  const { subtotal, totalDiscount, taxableAmount, vatTotal, totalDue } =
    calculateTotals();

  // Handle customer selection
  const handleCustomerSelect = (customerId) => {
    const customer = customers.find((c) => c.id === parseInt(customerId));
    if (customer) {
      setFormData({
        ...formData,
        customerId: customer.id,
        customerName: customer.name,
        customerTrn: customer.trn || "",
        customerPhone: customer.phone || "",
        customerAddress: customer.address || "",
        customerCity: customer.emirate || "",
        customerCountry: customer.country || "UAE",
      });
    }
  };

  // Add new item row
  const addItem = () => {
    setItems([
      ...items,
      {
        description: "",
        quantity: 1,
        unit: "Unit",
        unit_price: 0,
        discount_amount: 0,
        vat_percent: 5,
      },
    ]);
  };

  // Remove item row
  const removeItem = (index) => {
    if (items.length > 1) {
      const newItems = items.filter((_, i) => i !== index);
      setItems(newItems);
    }
  };

  // Update item field
  const updateItem = (index, field, value) => {
    const newItems = [...items];

    if (
      ["quantity", "unit_price", "discount_amount", "vat_percent"].includes(field)
    ) {
      newItems[index][field] = parseFloat(value) || 0;
    } else {
      newItems[index][field] = value;
    }

    setItems(newItems);
  };

  // Update form field
  const updateField = (field, value) => {
    setFormData({
      ...formData,
      [field]: value,
    });
  };

  // Format date for display
  const formatDateForDisplay = (dateString) => {
    if (!dateString) return "";
    const date = new Date(dateString);
    return date
      .toLocaleDateString("en-GB", {
        day: "2-digit",
        month: "2-digit",
        year: "numeric",
      })
      .replace(/\//g, "/");
  };

  // Validate form
  const validateForm = () => {
    const newErrors = {};

    if (!formData.customerName)
      newErrors.customerName = "Customer name is required";
    // Invoice number is auto-generated by backend, no need to validate
    if (!formData.invoiceDate) newErrors.invoiceDate = "Invoice date is required";

    // Validate payment mode is required when status is 'Paid'
    if (formData.status === "Paid" && !formData.mode) {
      newErrors.mode = "Payment mode is required when status is Paid";
    }

    items.forEach((item, index) => {
      if (!item.description) {
        newErrors[`item_${index}_description`] = "Description required";
      }
      if (item.quantity <= 0) {
        newErrors[`item_${index}_quantity`] = "Quantity must be greater than 0";
      }
    });

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Reset form to default values
  const resetForm = () => {
    const today = new Date();
    const tenDaysFromNow = new Date();
    tenDaysFromNow.setDate(today.getDate() + 10);

    setFormData({
      invoiceNo: "[AUTO]",
      invoiceDate: formatDateForInput(today),
      dueDate: formatDateForInput(tenDaysFromNow),
      mode: null,
      status: "Unpaid",
      customerId: null,
      customerName: "",
      customerTrn: "",
      customerPhone: "",
      customerAddress: "",
      customerCity: "",
      customerCountry: "",
      items: [],
      notes: "",
    });

    setItems([
      {
        description: "Service1",
        quantity: 1,
        unit: "Unit",
        unit_price: 0,
        discount_amount: 0,
        vat_percent: 5,
      },
    ]);

    setErrors({});
    setShowCustomerForm(false);
  };

  // Handle form submit - show modal first
  const handleSubmit = (e) => {
    e.preventDefault();
    if (validateForm()) {
      // Store form data and show modal for user to choose action
      setPendingFormData({ items, formData });
      setShowSaveModal(true);
    }
  };

  // Handle actual invoice saving
  const saveInvoice = async (downloadPDF = false) => {
    setShowSaveModal(false);
    
    try {
      // Prepare API payload matching Laravel migrations
      const apiPayload = {
        customer_id: formData.customerId,
        // NOTE: DO NOT send invoice_no - Laravel backend generates it automatically
        // invoice_no will be generated server-side from the Lastid table
          invoice_date: formData.invoiceDate,
          due_date: formData.dueDate,
          status: formData.status,
          mode: formData.status === "Unpaid" ? "" : (formData.mode || ""),
          notes: formData.notes,
          items: items.map((item) => ({
            description: item.description,
            quantity: parseFloat(item.quantity) || 0,
            unit: item.unit,
            unit_price: parseFloat(item.unit_price) || 0,
            discount_amount: parseFloat(item.discount_amount) || 0,
            vat_percent: parseFloat(item.vat_percent) || 0,
          })),
        };

        // Send data to Laravel API
        const apiUrl = process.env.REACT_APP_API_BASE_URL;
        const response = await axios.post(`${apiUrl}/api/invoices`, apiPayload);

        // Debug: log the full response to see structure
        console.log('===== API RESPONSE DEBUG =====');
        console.log('Full Response:', response);
        console.log('Response Status:', response.status);
        console.log('Response Data:', response.data);
        console.log('Response Data Keys:', Object.keys(response.data));
        
        // The Laravel backend returns the invoice object DIRECTLY or nested under 'invoice' key
        // First check if invoice is nested, otherwise use response.data directly
        let invoiceFromResponse = response.data.invoice || response.data;
        console.log('Invoice Object from Response:', invoiceFromResponse);
        
        if (invoiceFromResponse) {
          console.log('Invoice Keys:', Object.keys(invoiceFromResponse));
          console.log('Invoice invoice_no:', invoiceFromResponse.invoice_no);
        }
        
        // Extract invoice number - the key field is 'invoice_no' (snake_case from Laravel)
        // The backend generates this as 'INV-' . str_pad($newInvoiceId, 5, '0', STR_PAD_LEFT)
        let finalInvoiceNo = '[AUTO]';
        if (invoiceFromResponse && invoiceFromResponse.invoice_no) {
          finalInvoiceNo = invoiceFromResponse.invoice_no;
          console.log('✓ SUCCESS: Got invoice_no:', finalInvoiceNo);
        } else {
          console.error('✗ FAILED: Could not find invoice_no in response');
          console.error('Invoice object:', invoiceFromResponse);
        }
        
        console.log('Final Invoice Number to Use:', finalInvoiceNo);
        console.log('=============================');

        // Store API response for debugging
        setApiDebugResponse(response.data);

        // Update invoice number from API response - synchronously for display
        setFormData(prevData => ({
          ...prevData,
          invoiceNo: finalInvoiceNo
        }));

        // Prepare display data for PDF and onSubmit callback using actual invoice number
        const invoiceData = {
          ...formData,
          invoiceNo: finalInvoiceNo,
          items: items,
          company: company,
          totals: { subtotal, totalDiscount, taxableAmount, vatTotal, totalDue },
          formattedDates: {
            invoiceDate: formatDateForDisplay(formData.invoiceDate),
            dueDate: formatDateForDisplay(formData.dueDate),
          },
          apiPayload: apiPayload,
          apiResponse: response.data,
        };

        // Prepare PDF data with actual invoice number
        const pdfInvoiceData = {
          ...formData,
          invoiceNo: finalInvoiceNo,
          items: items,
          company: company,
          totals: { subtotal, totalDiscount, taxableAmount, vatTotal, totalDue },
        };

        console.log('===== BEFORE PDF TRIGGER =====');
        console.log('finalInvoiceNo value:', finalInvoiceNo);
        console.log('pdfInvoiceData.invoiceNo:', pdfInvoiceData.invoiceNo);
        console.log('invoiceData.invoiceNo:', invoiceData.invoiceNo);
        console.log('Complete pdfInvoiceData:', pdfInvoiceData);
        console.log('PDF Download Enabled:', downloadPDF);
        console.log('==============================');

        // Set PDF data and trigger download only if user selected the option
        if (downloadPDF) {
          setPdfData(pdfInvoiceData);
          setShouldDownload(true);
          
          console.log('===== AFTER setPdfData CALLED =====');
          console.log('State updates scheduled (async)');
          console.log('====================================');
        }

        // If onSubmit is provided and not the default empty function, use it
        if (onSubmit && onSubmit !== (() => {})) {
          onSubmit(invoiceData);
        } else {
          // Default behavior: show success message
          if (downloadPDF) {
            alert("Invoice saved successfully! PDF will be downloaded automatically.");
          } else {
            // Show custom success modal for "Save Invoice Only"
            setSuccessMessage(finalInvoiceNo);
          }
        }
      } catch (error) {
        console.error('Error saving invoice:', error);
        alert('Error saving invoice. Please try again.');
      }
    };

  // Effect to handle PDF download
  useEffect(() => {
    if (shouldDownload && pdfData) {
      console.log('===== PDF DOWNLOAD EFFECT TRIGGERED =====');
      console.log('shouldDownload:', shouldDownload);
      console.log('pdfData.invoiceNo:', pdfData.invoiceNo);
      console.log('PDF Filename:', `Invoice-${pdfData.invoiceNo || "New"}.pdf`);
      console.log('Full pdfData:', pdfData);
      console.log('=========================================');

      // Generate and download PDF
      const generateAndDownloadPDF = async () => {
        try {
          console.log('Starting PDF generation...');
          console.log('pdfData passed to InvoicePDFDocument:', pdfData);
          
          const blob = await pdf(
            <InvoicePDFDocument invoiceData={pdfData} />,
          ).toBlob();
          
          const url = URL.createObjectURL(blob);
          const link = document.createElement("a");
          link.href = url;
          const fileName = `Invoice-${pdfData.invoiceNo || "New"}.pdf`;
          link.download = fileName;
          
          console.log('PDF Generated Successfully');
          console.log('PDF Filename:', fileName);
          console.log('Triggering download...');
          
          document.body.appendChild(link);
          link.click();
          document.body.removeChild(link);
          URL.revokeObjectURL(url);

          console.log('PDF Download Completed');

          // Reset the trigger
          setShouldDownload(false);
          setPdfData(null);
        } catch (error) {
          console.error("Error generating PDF:", error);
          alert("Error generating PDF. Please try again.");
          setShouldDownload(false);
          setPdfData(null);
        }
      };

      generateAndDownloadPDF();
    }
  }, [shouldDownload, pdfData]);

  return (
    <div className="min-h-screen bg-gradient-to-br from-primary/5 via-secondary/5 to-accent/5 p-6">
       
      <div className="max-w-7xl mx-auto">
        {/* Header with animated gradient */}
        <div className="bg-[#23A19B] rounded-t-2xl p-8 text-white shadow-xl">
          <div className="flex justify-between items-center">
            <div>
              <h1 className="text-4xl font-bold mb-2">VAT Invoice Generator</h1>
              {/* <p className="text-white/80">UAE Tax Compliant | FTA Approved Format</p> */}
            </div>
            <div className="badge badge-lg bg-white/20 text-white border-0 p-4">
              <span className="text-2xl font-bold">🇦🇪</span>
            </div>
          </div>
        </div>

        {/* Main Form Card */}
        <div className="bg-base-100 rounded-b-2xl shadow-2xl p-8 mb-8 border-t-4 border-primary">
          <form onSubmit={handleSubmit} className="space-y-8">
            {/* Invoice Details Section */}
            <div className="bg-gradient-to-r from-primary/10 to-secondary/10 p-6 rounded-xl">
              <h3 className="text-xl font-bold text-primary mb-4 flex items-center gap-2">
                <span className="w-1 h-6 bg-primary rounded-full"></span>
                Invoice Information
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                <div className="form-control">
                  <label className="label">
                    <span className="label-text font-semibold flex items-center gap-1">
                      <span className="text-primary">📄</span> INVOICE NO.
                    </span>
                  </label>
                  <input
                    type="text"
                    className={`input input-bordered border-2 focus:border-primary bg-base-200 cursor-not-allowed ${errors.invoiceNo ? "input-error" : ""}`}
                    value={formData.invoiceNo}
                    readOnly
                    placeholder="[AUTO]"
                  />
                </div>

                <div className="form-control">
                  <label className="label">
                    <span className="label-text font-semibold flex items-center gap-1">
                      <span className="text-secondary">📅</span> INVOICE DATE
                    </span>
                  </label>
                  <input
                    type="date"
                    className={`input input-bordered border-2 focus:border-secondary ${errors.invoiceDate ? "input-error" : ""}`}
                    value={formData.invoiceDate}
                    onChange={(e) => updateField("invoiceDate", e.target.value)}
                  />
                  {errors.invoiceDate && <span className="text-error text-sm mt-1">{errors.invoiceDate}</span>}
                </div>

                <div className="form-control">
                  <label className="label">
                    <span className="label-text font-semibold flex items-center gap-1">
                      <span className="text-info">💰</span> PAYMENT STATUS
                    </span>
                  </label>
                  <select
                    className="select select-bordered border-2 focus:border-info"
                    value={formData.status}
                    onChange={(e) => updateField("status", e.target.value)}
                  >
                    <option value="Paid">Paid</option>
                    <option value="Unpaid">Unpaid</option>
                  </select>
                </div>

                <div className="form-control">
                  <label className="label">
                    <span className="label-text font-semibold">
                      MODE OF PAYMENT {formData.status === "Paid" && <span className="text-error">*</span>}
                    </span>
                  </label>
                  <select
                    className={`select select-bordered border-2 ${
                      formData.status === "Paid"
                        ? `focus:border-info ${errors.mode ? "select-error" : ""}`
                        : "opacity-50 cursor-not-allowed bg-base-200"
                    } ${errors.mode ? "select-error" : ""}`}
                    value={formData.mode || ""}
                    onChange={(e) =>
                      updateField("mode", e.target.value || null)
                    }
                    disabled={formData.status !== "Paid"}
                  >
                    <option value="">Select</option>
                    <option value="Cash">Cash</option>
                    <option value="Bank">Bank</option>
                    <option value="Cheque">Cheque</option>
                  </select>
                  {errors.mode && <span className="text-error text-sm mt-1">{errors.mode}</span>}
                </div>

                <div className="form-control hidden">
                  <label className="label">
                    <span className="label-text font-semibold flex items-center gap-1">
                      <span className="text-accent">⏰</span> DUE DATE
                    </span>
                  </label>
                  <input
                    type="date"
                    className="input input-bordered border-2 focus:border-accent"
                    value={formData.dueDate}
                    onChange={(e) => updateField("dueDate", e.target.value)}
                  />
                </div>
              </div>
            </div>

            {/* Customer Section */}
            <div className="bg-gradient-to-r from-secondary/10 to-accent/10 p-6 rounded-xl">
              <div className="flex justify-between items-center mb-4">
                <h3 className="text-xl font-bold text-secondary flex items-center gap-2">
                  <span className="w-1 h-6 bg-secondary rounded-full"></span>
                  Bill To — Customer
                </h3>
                <button
                  type="button"
                  className="btn btn-sm btn-outline btn-secondary"
                  onClick={() => setShowCustomerForm(!showCustomerForm)}
                >
                  {showCustomerForm ? "📋 Select Customer" : "➕ Add New"}
                </button>
              </div>

              {!showCustomerForm ? (
                <div className="form-control mb-4">
                  {customersLoading ? (
                    <div className="flex items-center gap-2">
                      <span className="loading loading-spinner loading-sm"></span>
                      Loading customers...
                    </div>
                  ) : customers.length > 0 ? (
                    <select
                      className="select select-bordered border-2"
                      onChange={(e) => handleCustomerSelect(e.target.value)}
                      defaultValue=""
                    >
                      <option value="" disabled>
                        Select a customer
                      </option>
                      {customers.map((customer) => (
                        <option key={customer.id} value={customer.id}>
                          {customer.name} - {customer.trn || "No TRN"}
                        </option>
                      ))}
                    </select>
                  ) : (
                    <div className="text-sm text-gray-500">
                      No customers available. Add a new customer or check your connection.
                    </div>
                  )}
                </div>
              ) : null}

              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                <div className="form-control">
                  <label className="label">
                    <span className="label-text font-semibold">
                      CUSTOMER NAME *
                    </span>
                  </label>
                  <input
                    type="text"
                    className={`input input-bordered border-2 ${errors.customerName ? "input-error" : ""}`}
                    value={formData.customerName}
                    readOnly
                    placeholder=""
                  />
                </div>

                <div className="form-control">
                  <label className="label">
                    <span className="label-text font-semibold">
                      TRN (TAX REG. NO.)
                    </span>
                  </label>
                  <input
                    type="text"
                    className="input input-bordered border-2 font-mono"
                    value={formData.customerTrn}
                    readOnly
                    placeholder="15-digit TRN"
                    maxLength="15"
                  />
                </div>

                <div className="form-control">
                  <label className="label">
                    <span className="label-text font-semibold">PHONE</span>
                  </label>
                  <input
                    type="tel"
                    className="input input-bordered border-2"
                    value={formData.customerPhone}
                    onChange={(e) =>
                      updateField("customerPhone", e.target.value)
                    }
                    placeholder="+971 50 123 4567"
                  />
                </div>

                <div className="form-control">
                  <label className="label">
                    <span className="label-text font-semibold">ADDRESS</span>
                  </label>
                  <input
                    type="text"
                    className="input input-bordered border-2"
                    value={formData.customerAddress}
                    onChange={(e) =>
                      updateField("customerAddress", e.target.value)
                    }
                    placeholder="Street, Area"
                  />
                </div>

                <div className="form-control">
                  <label className="label">
                    <span className="label-text font-semibold">
                      CITY / EMIRATE
                    </span>
                  </label>
                  <select
                    className="select select-bordered border-2 bg-base-200 cursor-not-allowed opacity-75"
                    value={formData.customerCity}
                    disabled
                  >
                    <option value="">{formData.customerCity || "Select..."}</option>
                    {emirates.map((emirate) => (
                      <option key={emirate} value={emirate}>
                        {emirate}
                      </option>
                    ))}
                  </select>
                  <p className="text-xs text-gray-500 mt-1">Auto-filled from customer profile</p>
                </div>

                <div className="form-control">
                  <label className="label">
                    <span className="label-text font-semibold">COUNTRY</span>
                  </label>
                  <input
                    type="text"
                    className="input input-bordered border-2"
                    value={formData.customerCountry}
                    readOnly
                    placeholder="UAE"
                  />
                </div>
              </div>
            </div>

            {/* Line Items Section */}
            <div className="bg-gradient-to-r from-accent/10 to-primary/10 p-6 rounded-xl">
              <h3 className="text-xl font-bold text-accent mb-4 flex items-center gap-2">
                <span className="w-1 h-6 bg-accent rounded-full"></span>
                Line Items
              </h3>

              <div className="overflow-x-auto">
                <table className="table table-zebra w-full">
                  <thead className="bg-gradient-to-r from-primary to-secondary text-white">
                    <tr>
                      <th className="rounded-tl-lg">#</th>
                      <th>DESCRIPTION</th>
                      <th>QTY</th>
                      <th>UNIT</th>
                      <th>UNIT PRICE</th>
                      <th>DISC</th>
                      <th>VAT %</th>
                      <th className="rounded-tr-lg">TOTAL</th>
                      <th></th>
                    </tr>
                  </thead>
                  <tbody>
                    {items.map((item, index) => {
                      const itemTotal = calculateItemTotal(item);

                      return (
                        <tr
                          key={index}
                          className="hover:bg-base-200 transition-colors"
                        >
                          <td className="font-bold text-primary">
                            {index + 1}
                          </td>
                          <td>
                            <input
                              type="text"
                              className="input input-bordered input-sm w-40"
                              value={item.description}
                              onChange={(e) =>
                                updateItem(index, "description", e.target.value)
                              }
                              placeholder="Description"
                            />
                          </td>
                          <td>
                            <input
                              type="number"
                              className="input input-bordered input-sm w-16"
                              value={item.quantity}
                              onChange={(e) =>
                                updateItem(index, "quantity", e.target.value)
                              }
                              min="1"
                              step="0.01"
                            />
                          </td>
                          <td>
                            <select
                              className="select select-bordered select-sm w-20"
                              value={item.unit}
                              onChange={(e) =>
                                updateItem(index, "unit", e.target.value)
                              }
                            >
                              <option>Unit</option>
                              <option>Hour</option>
                              <option>Day</option>
                              <option>Service</option>
                              <option>Piece</option>
                            </select>
                          </td>
                          <td>
                            <input



                              type="number"
                              className="input input-bordered input-sm w-20"
                              value={item.unit_price}
                              onChange={(e) =>
                                updateItem(index, "unit_price", e.target.value)
                              }
                              min="0"
                              step="0.01"
                            />
                          </td>
                          <td>
                            <div className="relative">
                              <input
                                type="number"
                                className="input input-bordered input-sm w-16"
                                value={item.discount_amount}
                                onChange={(e) =>
                                  updateItem(
                                    index,
                                    "discount_amount",
                                    e.target.value,
                                  )
                                }
                                min="0"
                                step="0.01"
                              />
                            </div>
                          </td>
                          <td>
                            <select
                              className="select select-bordered select-sm w-16"
                              value={item.vat_percent}
                              onChange={(e) =>
                                updateItem(index, "vat_percent", e.target.value)
                              }
                            >
                              <option value="0">0%</option>
                              <option value="5">5%</option>
                            </select>
                          </td>
                          <td className="text-right font-mono font-bold text-success">
                            AED {itemTotal.toFixed(2)}
                          </td>
                          <td>
                            <button
                              type="button"
                              className="btn btn-ghost btn-xs text-error hover:bg-error/20"
                              onClick={() => removeItem(index)}
                              disabled={items.length === 1}
                            >
                              ✕
                            </button>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>

              <button
                type="button"
                className="btn btn-outline btn-accent btn-sm mt-4 hover:scale-105 transition-transform"
                onClick={addItem}
              >
                ➕ Add Line Item
              </button>
            </div>

            {/* Summary Cards */}
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
              <div className="stat bg-gradient-to-br from-info/20 to-info/5 rounded-xl">
                <div className="stat-figure text-info">
                  <span className="text-2xl">💰</span>
                </div>
                <div className="stat-title">Subtotal</div>
                <div className="stat-value text-info text-2xl">
                  AED {subtotal.toFixed(2)}
                </div>
              </div>

              <div className="stat bg-gradient-to-br from-warning/20 to-warning/5 rounded-xl">
                <div className="stat-figure text-warning">
                  <span className="text-2xl">🏷️</span>
                </div>
                <div className="stat-title">Discount</div>
                <div className="stat-value text-warning text-2xl">
                  AED {totalDiscount.toFixed(2)}
                </div>
              </div>

              <div className="stat bg-gradient-to-br from-secondary/20 to-secondary/5 rounded-xl">
                <div className="stat-figure text-secondary">
                  <span className="text-2xl">🧾</span>
                </div>
                <div className="stat-title">VAT (5%)</div>
                <div className="stat-value text-secondary text-2xl">
                  AED {vatTotal.toFixed(2)}
                </div>
              </div>

              <div className="stat bg-gradient-to-br from-success/20 to-success/5 rounded-xl">
                <div className="stat-figure text-success">
                  <span className="text-2xl">💵</span>
                </div>
                <div className="stat-title">Total Due</div>
                <div className="stat-value text-success text-2xl">
                  AED {totalDue.toFixed(2)}
                </div>
              </div>
            </div>

            {/* Company Info and VAT Checklist */}
           

              {/* VAT Checklist */}
            

            {/* Notes & Bank Details */}
            <div className="grid grid-cols-1 gap-6">
              <div className="bg-gradient-to-br from-info/10 to-info/5 p-6 rounded-xl">
                <label className="label">
                  <span className="label-text font-bold text-info text-lg flex items-center gap-2">
                    <span className="w-1 h-6 bg-info rounded-full"></span>
                    NOTES / TERMS
                  </span>
                </label>
                <textarea
                  className="textarea textarea-bordered h-32 w-full border-2 focus:border-info"
                  value={formData.notes}
                  onChange={(e) => updateField("notes", e.target.value)}
                  placeholder=""
                ></textarea>
              </div>
            </div>

            {/* Form Actions */}
            <div className="flex justify-end gap-4 pt-6 border-t">
              <button
                type="button"
                className="btn btn-ghost btn-lg hover:bg-base-200"
                onClick={() => window.history.back()}
              >
                Cancel
              </button>
              <button
                type="submit"
                className="btn btn-primary btn-lg bg-gradient-to-r from-primary to-secondary border-0 hover:scale-105 transition-transform shadow-lg"
              >
                💾 Save Invoice
              </button>
            </div>
          </form>
        </div>

        {/* Hidden PDF Download Link */}
        {pdfData && (
          <div style={{ display: "none" }}>
            <PDFDownloadLink
              document={<InvoicePDFDocument invoiceData={pdfData} />}
              fileName={`Invoice-${pdfData.invoiceNo || "New"}.pdf`}
            >
              {({ loading }) => !loading && "Download PDF"}
            </PDFDownloadLink>
          </div>
        )}

        {/* Footer */}
        <div className="text-center text-sm text-base-500 mt-4">
          <p>
            🇦🇪 UAE VAT Compliant | Status: {formData.status} | Mode: {formData.mode}
          </p>
        </div>

        {/* DEBUG: API Response Display */}
        {/* {apiDebugResponse && (
          <div className="mt-8 p-6 bg-gray-900 text-green-400 rounded-lg font-mono text-xs overflow-auto max-h-96">
            <div className="mb-3 flex justify-between items-center">
              <div className="font-bold text-lg text-green-300">📋 API Response (Debugging):</div>
              <button
                type="button"
                onClick={() => setApiDebugResponse(null)}
                className="btn btn-sm btn-ghost text-red-400 hover:text-red-600"
              >
                ✕ Close
              </button>
            </div>
            <pre className="whitespace-pre-wrap break-words">
              {JSON.stringify(apiDebugResponse, null, 2)}
              
            </pre>
          </div>
        )} */}

        {/* Save Options Modal */}
        {showSaveModal && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-white rounded-lg shadow-2xl p-8 max-w-md w-full mx-4">
              <div className="mb-6">
                <h2 className="text-2xl font-bold text-primary mb-2">Save Invoice</h2>
                <p className="text-gray-600">What would you like to do?</p>
              </div>

              <div className="space-y-4">
                {/* Option 1: Save Only */}
                <button
                  onClick={() => saveInvoice(false)}
                  className="w-full btn btn-outline btn-lg border-2 border-primary text-primary hover:bg-primary hover:border-primary"
                >
                  <span className="text-xl mr-2">💾</span>
                  Save Invoice Only
                </button>

                {/* Option 2: Save & Print PDF */}
                <button
                  onClick={() => saveInvoice(true)}
                  className="w-full btn btn-primary btn-lg bg-gradient-to-r from-primary to-secondary border-0"
                >
                  <span className="text-xl mr-2">📄</span>
                  Save & Print PDF
                </button>
              </div>

              <div className="mt-6 pt-6 border-t text-center">
                <button
                  onClick={() => setShowSaveModal(false)}
                  className="btn btn-ghost btn-sm"
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Success Message Modal */}
        {successMessage && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-white rounded-lg shadow-2xl p-8 max-w-md w-full mx-4">
              <div className="mb-6 text-center">
                <div className="text-6xl text-success mb-4">✓</div>
                <h2 className="text-2xl font-bold text-success mb-2">Invoice Saved Successfully</h2>
                <p className="text-lg text-primary font-semibold">
                  Invoice No: <span className="text-secondary font-bold">{successMessage}</span> has been saved
                </p>
              </div>

              <div className="mt-6 text-center">
                <button
                  onClick={() => {
                    setSuccessMessage(null);
                    setFormData(prevData => ({
                      ...prevData,
                      invoiceNo: "[AUTO]"
                    }));
                  }}
                  className="btn btn-primary btn-lg"
                >
                  Create New Invoice
                </button>
              </div>
            </div>
          </div>
        )}
      </div>

      <style jsx>{``}</style>
    </div>
  );
};

export default ProfessionalVATInvoice;
